import { html, nothing, type PropertyValues } from 'lit';
import { Historian } from '@vitesse/proto/vitesse/v1/historian_pb.js';
import { customElement, property, state } from 'lit/decorators.js';
import { BasePage } from '@vitesse/base-ui/components/base-page';
import { Models } from '@vitesse/proto/vitesse/v1/model_pb.js';
import { Integrations } from '@vitesse/proto/vitesse/v1/integrations_pb.js';
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
import { getSession } from '@vitesse/base-ui';
import '@vitesse/base-ui/elements/vitesse-editable-description';
import type { ModelManager } from '../managers/model-manager';
import type { ControllerManager } from '../managers/controller-manager';
import type { IntegrationManager } from '../managers/integration-manager';
import type { GatewayEntry } from '../managers/gateway-entry';
import type { ModelView } from '../../core/domain/model';
import type { CheckOptions, StatePorts } from '@vitesse/statechart-host';
import { childPorts, portTypes } from '../ai-tools/port-types';
import { profileOf } from '../ai-tools/model-draft';
import type { BackendClient } from '../bridge/backend-client';
import { statusBadge, type PillTab } from '@vitesse/base-ui/components/templates';
import { statusColor, modelStatusLabel, isModelActive } from '../components/model/model-status';
import { forwardsForModel, forwardsColor, type ModelForward } from '../components/model/model-forwards';
import { loadStatePorts } from '../components/model/state-tree-ports';
import { stateTree, type StateTree } from '../components/model/state-tree';
import { button } from '@vitesse/base-ui/components/base-buttons';
import { t } from '@vitesse/base-ui/i18n';
import { fmtAgo } from '@vitesse/base-ui/format';
import type { ModelLog, HistorySample, ModelSource } from '../../core/bridge/connect-client';
import { type LogLine } from '../components/log-tree';
import '../components/log-tree';
import '../components/model/model-commands-section';
import '../components/model/model-variables-section';
import '../components/model/model-bindings-section';
import '../components/model/model-arming-section';
import '../components/model/model-activity-section';
import '../components/model/model-machines-section';
import '../components/model/model-simulate-section';
import { forwardStateLabel } from '../components/model/model-streaming-tab';
import '@vitesse/base-ui/elements/vitesse-confirm-dialog';
import './vitesse-statechart';
import './vitesse-code-browser';
import type { CodeBlock } from './vitesse-code-browser';
import './vitesse-panel-grid';
import type { PanelDef, PanelLayoutItem } from './vitesse-panel-grid';

/* The model detail sub-views — a 1:1 mirror of the controller (symbol) page:
 * Statechart (the live workspace) · SCXML (the source) · Logs (verbatim) · Streaming (where the
 * states and the status go, only when a Forward reads the model). */
type ModelTab = 'statechart' | 'scxml' | 'logs' | 'streaming';

/* Default workspace tiles (12-col grid; the spanning row count is `WORKSPACE_ROWS`
 * so the grid FILLS the tab). Persisted user overrides come from localStorage. */
const WORKSPACE_ROWS = 9;
// Statechart RIGHT (full height); Commands / Bindings / Variables stack LEFT.
// (array order is the toolbar toggle order.)
const WORKSPACE_PANELS: PanelDef[] = [
  { id: 'statechart', title: 'Statechart', x: 4, y: 0, w: 8, h: WORKSPACE_ROWS, minW: 4, minH: 4 },
  { id: 'commands', title: 'Commands', x: 0, y: 0, w: 4, h: 2, minW: 2, minH: 1 },
  { id: 'bindings', title: 'Bindings', x: 0, y: 2, w: 4, h: 3, minW: 2, minH: 2 },
  { id: 'variables', title: 'Variables', x: 0, y: 5, w: 4, h: 3, minW: 2, minH: 2 },
  // What the model has DONE with the authority of the person who armed it. Who that is shows in
  // the tab row (renderTabTrailing), on every tab, because it is a fact about the whole model.
  { id: 'activity', title: 'AI activity', x: 0, y: 8, w: 4, h: 2, minW: 2, minH: 1 },
  // What it WOULD have done. Directly under the live statechart, the same width, because it is the
  // same picture asked about the past — and it needs the room: a chart, a transport and the lanes.
  { id: 'simulate', title: 'Simulate', x: 4, y: WORKSPACE_ROWS, w: 8, h: 8, minW: 4, minH: 4 },
  // A group's machines, each with its state and ports (Part 7 of the hierarchical models plan).
  // Only a group has the tile: the statechart draws the root, and this lists the rest. The full width
  // under Simulate, so the diagram of the whole group fits without scrolling.
  { id: 'machines', title: 'Machines', x: 0, y: WORKSPACE_ROWS + 8, w: 12, h: 5, minW: 2, minH: 2 },
];
/** The tiles a model has: a single model has no machines tile. */
function panelsFor(m: ModelView): PanelDef[] {
  return m.children.length > 0 ? WORKSPACE_PANELS : WORKSPACE_PANELS.filter((p) => p.id !== 'machines');
}
/** Per-USER layout (shared across every model — "how I like my workspace"). */
const WORKSPACE_LAYOUT_KEY = 'vitesse-model-workspace-layout';
/** Per-USER too: the ghost tree of the Statechart tile is on or off for every model. */
const GHOST_TREE_KEY = 'vitesse-model-ghost-tree';

/*
 * <vitesse-model-detail> — the model detail page, rebuilt to mirror the symbol
 * page. LIGHT DOM; extends BasePage so it shares the exact back·title·badges·
 * actions·pill-tabs chrome. Three tabs:
 *   1. Statechart — a draggable/resizable WORKSPACE (<vitesse-panel-grid>) of
 *      tiles: the live statechart, a Variables panel (charts), and the model's
 *      declared Commands. The model's debugger view.
 *   2. SCXML — the source, in the program-view's code experience (Monaco +
 *      minimap + folding), read-only.
 *   3. Logs — the model's IP-safe log tail, the SAME shared <vitesse-log-tree>
 *      the controller Logs tab uses (pixel-identical).
 * The header carries lifecycle (Start/Stop) + Download (the SCXML) + Export
 * (variable history CSV). The Interface lives in the Flows section, not here;
 * recording is gone (the historian blanket-captures in v2).
 */
@customElement('vitesse-model-detail')
export class ModelDetail extends BasePage {
  @property({ attribute: false }) backend?: BackendClient;
  @property({ attribute: false }) models?: ModelManager;
  /** Parent gateway's controllers — the binding editor's symbol-picker source. */
  @property({ attribute: false }) controllers?: ControllerManager;
  /** Parent gateway's integrations — the source of the Forwards that send this model's data out. */
  @property({ attribute: false }) integrations?: IntegrationManager;
  /** Org gateways — the binding picker's source pane is the shell's own tree. */
  @property({ attribute: false }) gateways: GatewayEntry[] = [];
  @property() modelId = '';
  /* The AI plane's client + who is looking — the arming control's inputs. Passed DOWN rather than
   * reached for, so the control stays a pure renderer and the shell keeps owning identity. */
  @property({ attribute: false }) aiApi?: import('@vitesse/base-ui/ai').AiApi;
  /** The org's expert roster, for the SCXML editor's `expert` completions. Passed in rather than
   *  fetched: the shell already holds the AI config, and a page should not re-read it. */
  @property({ attribute: false }) experts: { id: string; description: string }[] = [];
  @property() userId = '';
  @property({ type: Boolean }) canAdmin = false;
  /** Parent gateway id (scopes backend lookups). */
  @property() gatewayId = '';
  /** Parent gateway display name — the back-button label. */
  @property() gatewayName = '';

  @state() private model?: ModelView;
  @state() private busy = false;
  /** The delete confirmation, and what the fabric said if it refused. */
  @state() private confirmDelete = false;
  @state() private deleting = false;
  @state() private deleteError = '';
  @state() private scxml = '';
  /** The saved model's profile, from the same read as `scxml`: the code view checks with it. */
  @state() private profile?: 'hard';
  @state() private tab: ModelTab = 'statechart';
  /** Persisted workspace layout (per-user, shared across models). */
  @state() private layout?: PanelLayoutItem[];
  /** Workspace widgets toggled OFF (trash / toolbox). Re-added via the toolbox. */
  @state() private hiddenPanels = new Set<string>();
  /** The Forwards that send this model's states and status to an outside system. */
  @state() private forwards: ModelForward[] = [];
  /** The ghost tree of the Statechart tile (docs/state-subgraph-plan.md): the machines under a root state. */
  @state() private ghost = false;
  /** The ports of each state of the root's text, for the ghost tree: undefined while they load, null if they could not. */
  @state() private treePorts?: Map<string, StatePorts> | null;
  /** What `treePorts` is for: the root's text and the descriptor's revision. A new load replaces the object. */
  private treePortsFor = { scxml: '', revision: -1 };
  /** The root state that the ghost tree hangs under: the active one when the tree turns on, then the one a person picks. */
  @state() private treeState = '';
  /** Each template's text by its name, from the source read. Null when the read has no descriptor (a relay older than Part 5). */
  @state() private templates: Record<string, string> | null = null;
  /** The number of the latest source read: only its answer is applied. */
  private sourceRead = 0;
  /** The model's descriptor revision when the latest source read started. A save writes a new one. */
  private sourceRevision = 0;

  private readonly onChange = (): void => {
    this.model = this.models?.get(this.modelId);
  };
  private readonly onIntegrationsChange = (): void => {
    this.forwards = forwardsForModel(this.integrations?.all ?? [], this.gatewayId, this.modelId);
    // The Streaming tab goes with the last Forward of the model (a removal, or another model).
    if (this.forwards.length === 0 && this.tab === 'streaming') this.tab = 'statechart';
  };

  connectedCallback(): void {
    super.connectedCallback();
    this.layout = this.readLayout();
    this.ghost = this.readGhost();
    this.models?.addEventListener('change', this.onChange);
    this.integrations?.addEventListener('change', this.onIntegrationsChange);
    this.onChange();
    this.onIntegrationsChange();
  }
  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.models?.removeEventListener('change', this.onChange);
    this.integrations?.removeEventListener('change', this.onIntegrationsChange);
  }

  // On model→model navigation (Lit reuses this element): refresh the view + fetch
  // the SCXML body (stripped from the lean Subscribe stream; usually warm from a
  // card hover). In willUpdate, not updated — setting state post-update loops.
  protected willUpdate(changed: PropertyValues): void {
    // COLD RELOAD: the per-gateway ModelManager is created only once the gateway
    // connects, which lands a tick or two AFTER the route resolves — so `models`
    // arrives (undefined → manager) post-mount. connectedCallback ran while it was
    // undefined, so the 'change' listener never attached, and the manager's later
    // async model-load fires into the void → the view stays on "Model not found"
    // until a re-nav. Re-bind the listener whenever `models` changes so the load
    // is heard. (The controller page sidesteps this by receiving a re-resolved
    // object prop; the model page self-resolves, so it must own the subscription.)
    if (changed.has('models')) {
      (changed.get('models') as ModelManager | undefined)?.removeEventListener('change', this.onChange);
      this.models?.addEventListener('change', this.onChange);
    }
    // The integrations manager can also arrive after the mount (the same cold reload).
    if (changed.has('integrations')) {
      (changed.get('integrations') as IntegrationManager | undefined)?.removeEventListener('change', this.onIntegrationsChange);
      this.integrations?.addEventListener('change', this.onIntegrationsChange);
    }
    if (changed.has('integrations') || changed.has('modelId') || changed.has('gatewayId')) this.onIntegrationsChange();
    if (changed.has('modelId')) {
      this.treePorts = undefined;
      this.treePortsFor = { scxml: '', revision: -1 };
      this.treeState = '';
      this.templates = null;
    }
    if (changed.has('modelId') && this.modelId) {
      this.onChange();
      this.scxml = '';
      this.profile = undefined;
      void this.loadSource();
    } else if (changed.has('models') && this.modelId) {
      this.onChange();
      if (!this.scxml) void this.loadSource();
    }
    // A save (an AI edit, a binding, a Start or a Stop) gives the descriptor a new revision. Read the
    // source again, past the worker's cache, or the statechart, the bindings, the Simulate tile and the
    // SCXML tab keep the document from before the save. The cache can hold that document: the AI reads
    // the source just before it writes.
    if (this.sourceRead > 0 && (this.model?.sourceRevision ?? 0) !== this.sourceRevision) void this.loadSource(true, true);
    if (this.ghost) void this.loadTreePorts();
    // The tree hangs under the active state when it turns on or its ports arrive, and under the state
    // that a person picks after that. If a save removed that state, the active state takes its place.
    const ports = this.treePorts;
    if (this.ghost && ports && this.model && !ports.has(this.treeState)) this.treeState = firstTreeState(this.model.currentState, ports);
  }

  /*
   * THE PORTS OF EACH ROOT STATE, for the ghost tree (Step 3 of docs/state-subgraph-plan.md).
   *
   * Loaded only while the tree is on: the engine host is large, and a person who never turns the
   * tree on must not pay for it. 1 result for each text and revision of the descriptor. A save
   * changes both, and the page then reads the source again. The page renders on each live tick, so
   * the check before a load compares 2 fields and nothing more.
   */
  private async loadTreePorts(): Promise<void> {
    const m = this.model;
    if (!m || m.children.length === 0 || !this.scxml) return;
    if (this.treePortsFor.scxml === this.scxml && this.treePortsFor.revision === m.sourceRevision) return;
    const asked = { scxml: this.scxml, revision: m.sourceRevision };
    this.treePortsFor = asked;
    const ports = await loadStatePorts(asked.scxml, m);
    // Only the latest load counts: a save, or another model, asked again while it ran.
    if (this.treePortsFor === asked) this.treePorts = ports;
  }
  /** Where the load of the tree's ports is, on the tile as `data-ghost-tree`: a check in a browser waits for 'ready'. */
  private treeLoad(): 'loading' | 'ready' | 'failed' {
    return this.treePorts === undefined ? 'loading' : this.treePorts === null ? 'failed' : 'ready';
  }

  private async loadSource(retry = true, fresh = false): Promise<void> {
    const id = this.modelId;
    if (!id || !this.models) return;
    const read = ++this.sourceRead;
    this.sourceRevision = this.model?.sourceRevision ?? 0;
    try {
      const src = await this.models.getSource(id, fresh);
      // Only the latest read counts: an older one is of another model, or of the document before a save.
      if (read === this.sourceRead) {
        this.scxml = src.scxml;
        // The list's view of a model has no profile; the saved source's descriptor has it.
        this.profile = profileOf(src.descriptor).profile;
        this.templates = templatesOf(src.descriptor);
      }
    } catch {
      // Transient worker hiccup / reconnect — retry once before the placeholder.
      if (retry && read === this.sourceRead) setTimeout(() => void this.loadSource(false, fresh), 1000);
    }
  }

  // ── workspace layout persistence (per-user) ────────────────────────────────
  private readLayout(): PanelLayoutItem[] | undefined {
    try {
      const v = localStorage.getItem(WORKSPACE_LAYOUT_KEY);
      return v ? (JSON.parse(v) as PanelLayoutItem[]) : undefined;
    } catch {
      return undefined;
    }
  }
  private readonly onLayoutChange = (e: Event): void => {
    const detail = (e as CustomEvent<PanelLayoutItem[]>).detail;
    try {
      localStorage.setItem(WORKSPACE_LAYOUT_KEY, JSON.stringify(detail));
    } catch {
      /* storage full / disabled — the layout just isn't persisted */
    }
  };

  // ── the ghost toggle (per-user) ────────────────────────────────────────────
  private readGhost(): boolean {
    try {
      return localStorage.getItem(GHOST_TREE_KEY) === '1';
    } catch {
      return false;
    }
  }
  private toggleGhost(): void {
    this.ghost = !this.ghost;
    // Turned on again, the tree hangs under the active state again.
    if (!this.ghost) this.treeState = '';
    try {
      localStorage.setItem(GHOST_TREE_KEY, this.ghost ? '1' : '0');
    } catch {
      /* storage disabled — the choice lasts until the page closes */
    }
  }

  private nav(): void {
    this.dispatchEvent(new CustomEvent('navigate', { detail: { view: 'gateway' }, bubbles: true, composed: true }));
  }

  private async toggleRun(): Promise<void> {
    const m = this.model;
    if (!this.models || !m || this.busy) return;
    this.busy = true;
    try {
      await this.models.execute(this.modelId, isModelActive(m.status) ? 'stop' : 'start');
    } finally {
      this.busy = false;
    }
  }

  /** Download the model's SCXML source. */
  private downloadModel(): void {
    if (!this.scxml) return;
    triggerDownload(`${safeName(this.model?.name || this.modelId)}.scxml`, this.scxml, 'application/xml');
  }

  /** Export the model's published-variable history as CSV (the historian blanket-
   *  captures model variables). Outputs are the model's published surface.
   *
   *  A click ALWAYS yields a file, never a silent no-op. A model with no declared
   *  outputs publishes nothing, so it has no variable history, and its export is
   *  a valid header-only CSV: "empty is fine".
   *  The historian query is SKIPPED in that case, because queryRange with no
   *  symbols is a `symbols required` error, not an empty result — so the empty
   *  file is built directly rather than by round-tripping a doomed query. */
  private async exportData(): Promise<void> {
    const m = this.model;
    if (!this.models || !m) return;
    let samples: HistorySample[] = [];
    if (m.outputs.length > 0) {
      // A model WITH declared outputs queries its published-variable history; a
      // failure here is non-fatal (the user can retry) and leaves samples empty,
      // so the click still yields a file rather than a silent nothing.
      samples = await this.models.queryHistory(this.modelId, m.outputs, 5 * 60_000).catch(() => []);
    }
    triggerDownload(`${safeName(m.name || this.modelId)}.csv`, samplesToCsv(samples), 'text/csv');
  }

  // ── BasePage hooks ─────────────────────────────────────────────────────────
  protected pageTitle(): string {
    return this.model?.name || this.modelId;
  }
  protected titleIcon(): string {
    return 'git-compare-arrows';
  }
  protected renderDescription() {
    const m = this.model;
    if (!m) return nothing;
    // Editable for EVERY model, interface or not: the description is the model's
    // OWN top-level field now, not the interface's — so editing it can never give
    // a model with no interface a declared-and-empty one. Gated on model:edit.
    const canEdit = getSession()?.allowsRpc(Models.method.update) !== false;
    return html`<vitesse-editable-description
      .value=${m.description}
      placeholder=${t('model.noDescription')}
      hint=${t('dashboards.descriptionEditHint')}
      .canEdit=${canEdit}
      .commit=${(next: string) => this.commitDescription(next)}
    ></vitesse-editable-description>`;
  }

  private commitDescription(next: string): void {
    const m = this.model;
    if (!m || !this.models) return;
    // Field-masked write: ONLY the top-level description travels. The model-runner
    // merges just that field into the descriptor it holds — the SCXML, interface,
    // wiring and desired state are untouched, and a no-interface model keeps
    // no interface.
    void this.models
      .update(m.id, { name: '', scxml: '', desiredState: '', description: next, updateMask: ['description'] })
      .catch(() => {
        /* transient write failure — the live stream stays the source of truth */
      });
  }

  protected renderBadges() {
    const m = this.model;
    return m ? statusBadge(statusColor(m.status), modelStatusLabel(m.status)) : nothing;
  }
  // Download · Export (text + icon, blue-on-hover) come BEFORE the Start/Stop
  // lifecycle button. They no-op until ready (scxml loaded / outputs declared)
  // — usually instant, so we keep them visible rather than shift the row.
  protected renderActions() {
    const m = this.model;
    if (!m) return nothing;
    const active = isModelActive(m.status);
    return html`${button({
        label: t('model.download'),
        icon: 'download',
        color: 'blue',
        onClick: () => this.downloadModel(),
      })}<vitesse-gated .rpc=${Historian.method.queryRange} inline
        >${button({
          label: t('model.export'),
          icon: 'database-arrow-down',
          color: 'blue',
          onClick: () => void this.exportData(),
        })}</vitesse-gated
      ><vitesse-gated .rpc=${Models.method.executeCommand} inline
        >${button({
          label: active ? t('model.stopModel') : t('model.startModel'),
          icon: active ? 'power' : 'play',
          color: active ? 'red' : 'blue',
          onClick: () => void this.toggleRun(),
          disabled: this.busy,
        })}</vitesse-gated
      ><vitesse-gated .rpc=${Models.method.delete} inline
        >${button({
          label: t('model.delete'),
          icon: 'trash-2',
          color: 'red',
          onClick: () => (this.confirmDelete = true),
          disabled: this.busy,
        })}</vitesse-gated
      >`;
  }

  /*
   * WHERE THE DATA GOES: the Streaming tab.
   *
   * A Forward of an integration on this gateway sends the states and the status of this model to a
   * table in an outside system. The tab shows only when a Forward reads this model. Its dot keeps the
   * color of the worst Forward at rest, so a person sees a stopped Forward without a click. Its count
   * is the values that no table has yet, so an outage shows as a number that goes up. Its title names
   * each target and says how it is.
   */
  private streamingTab(): PillTab {
    return {
      id: 'streaming',
      label: t('model.streaming'),
      icon: 'database-arrow-up',
      status: forwardsColor(this.forwards),
      count: this.forwards.reduce((n, f) => n + (f.status?.lag ?? 0), 0),
      title: this.forwards
        .map((f) =>
          t('model.streamingTabTitle', {
            name: f.integration.typeLabel || f.integration.displayName || f.integration.id,
            state: forwardStateLabel(f.state),
            write: f.status?.lastWriteMs ? t('model.forwardLastWrite', { ago: fmtAgo(f.status.lastWriteMs) }) : t('model.forwardNoWrite'),
          }),
        )
        .join('\n'),
    };
  }

  /*
   * DELETE — behind its own permission, and behind a confirmation.
   *
   * `model:delete` is not `model:edit`: removing an automaton and rewriting one are different
   * powers, and the gate reflects that. The dialog is the same one the integration page uses, for
   * the same reason — this ends something that may be driving a machine, and the sentence a person
   * reads before agreeing should say so.
   */
  private async runDelete(): Promise<void> {
    const m = this.model;
    if (!m || this.deleting) return;
    this.deleting = true;
    this.deleteError = '';
    try {
      await this.models?.delete(this.modelId);
      this.confirmDelete = false;
      // Back to the gateway: staying on the detail page of something that no longer exists would
      // leave the reader looking at a shell of it.
      this.nav();
    } catch (e) {
      this.deleteError = e instanceof Error ? e.message : String(e);
    } finally {
      this.deleting = false;
    }
  }

  protected onBack(): void {
    this.nav();
  }
  protected backLabel(): string {
    return this.gatewayName || t('model.back');
  }
  protected backAriaLabel(): string {
    return t('gateway.ariaBack');
  }
  // AI hints follow the active tab.
  protected aiHints(): string[] {
    switch (this.tab) {
      case 'scxml':
        return [t('model.hintLogic'), t('model.hintState')];
      case 'logs':
        return [t('controller.hintLogsRecent'), t('controller.hintLogsError')];
      case 'streaming':
        return [t('model.hintStreamingArrive'), t('model.hintStreamingError')];
      case 'statechart':
      default:
        return [t('model.hintState'), t('model.hintLogic')];
    }
  }

  protected tabs(): PillTab[] {
    return [
      { id: 'statechart', label: t('model.statechart'), icon: 'git-compare-arrows' },
      { id: 'scxml', label: t('model.scxmlView'), icon: 'code-xml' },
      { id: 'logs', label: t('model.logs'), icon: 'scroll-text' },
      ...(this.forwards.length > 0 ? [this.streamingTab()] : []),
    ];
  }
  protected activeTabId(): string {
    return this.tab;
  }
  protected onTabChange(id: string): void {
    if (id !== this.tab) this.tab = id as ModelTab;
  }
  // Who the model acts as when it runs unattended: one status and its button after the tabs.
  protected renderTabTrailing() {
    if (!this.model || !this.aiApi) return nothing;
    return html`<vitesse-model-arming .api=${this.aiApi} modelId=${this.modelId} userId=${this.userId} ?canAdmin=${this.canAdmin}></vitesse-model-arming>`;
  }

  protected renderContent() {
    const m = this.model;
    if (!m) {
      return html`<div class="home-empty-gateways"><span class="home-empty-hint">${t('model.notFound')}</span></div>`;
    }
    return html`
      ${this.tab === 'statechart' ? this.renderWorkspace(m) : nothing} ${this.tab === 'scxml' ? this.renderScxml() : nothing}
      ${this.tab === 'logs'
        ? html`<vitesse-gate-overlay
            .rpc=${Models.method.subscribeLogs}
            context=${`gateways/${this.gatewayId}/models/${this.modelId}`}
            .content=${this.renderLogs()}
          ></vitesse-gate-overlay>`
        : nothing}
      ${this.tab === 'streaming'
        ? html`<vitesse-gate-overlay
            .rpc=${Integrations.method.read}
            context=${`gateways/${this.gatewayId}/models/${this.modelId}`}
            .content=${html`<vitesse-model-streaming
              .model=${m}
              .forwards=${this.forwards}
              .integrations=${this.integrations}
              gatewayId=${this.gatewayId}
            ></vitesse-model-streaming>`}
          ></vitesse-gate-overlay>`
        : nothing}
      ${
        this.confirmDelete
          ? html`<vitesse-confirm-dialog
              .open=${true}
              heading=${t('model.deleteHeading')}
              message=${t('model.deleteMessage', { name: m.name })}
              detail=${isModelActive(m.status) ? t('model.deleteDetailRunning') : t('model.deleteDetail')}
              confirm-label=${t('model.delete')}
              danger
              ?busy=${this.deleting}
              error=${this.deleteError}
              @confirm=${() => void this.runDelete()}
              @dialog-close=${() => (this.confirmDelete = false)}
            ></vitesse-confirm-dialog>`
          : nothing
      }
    `;
  }

  private panelLabel(id: string): string {
    switch (id) {
      case 'commands':
        return t('model.commands');
      case 'variables':
        return t('model.variables');
      case 'statechart':
        return t('model.statechart');
      case 'bindings':
        return t('model.bindings');
      case 'simulate':
        return t('model.simulate');
      case 'machines':
        return t('model.machines');
      case 'activity':
        return t('model.activityTile');
      default:
        return id;
    }
  }
  private togglePanel(id: string): void {
    const next = new Set(this.hiddenPanels);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    this.hiddenPanels = next;
  }
  private readonly onPanelRemove = (e: Event): void => {
    const id = (e as CustomEvent<string>).detail;
    if (this.hiddenPanels.has(id)) return;
    const next = new Set(this.hiddenPanels);
    next.add(id);
    this.hiddenPanels = next;
  };

  // ── Statechart workspace — the symbol-tree frame (vg-wrapper + vg-toolbar) with
  //    a draggable widget CANVAS inside. The toolbar carries one base-button
  //    toggle per widget: present → red-on-hover (remove), absent → blue (add).
  /*
   * THE GHOST TREE FOR THE STATECHART (Step 7 of docs/state-subgraph-plan.md): the tree under the
   * picked root state, and the 1 line that says why a part is missing. Each render makes it again
   * from the live view, so the live states of level 2 follow the model. The statechart lays the
   * tree out again only when more than the live states changed.
   */
  private ghostTree(m: ModelView): { tree: StateTree | null; note: string } {
    if (this.treePorts === null) return { tree: null, note: t('model.ghostTreeFailed') };
    if (!this.treePorts || !this.treeState) return { tree: null, note: '' };
    const tree = stateTree(m, isModelActive(m.status), this.treePorts, this.templates ?? {}, this.treeState);
    if (!tree) return { tree: null, note: '' };
    if (tree.machines.length === 0 && tree.leaves.length === 0) {
      return { tree: null, note: t('model.ghostTreeEmpty', { state: this.treeState }) };
    }
    // An older relay sends no template text: level 1 only.
    return { tree, note: this.templates === null && tree.machines.length > 0 ? t('model.ghostTreeNoCharts') : '' };
  }
  private readonly onStateSelect = (e: Event): void => {
    this.treeState = (e as CustomEvent<string>).detail;
  };

  private renderWorkspace(m: ModelView) {
    const active = isModelActive(m.status) ? m.currentState : '';
    const ghost = this.ghost && m.children.length > 0;
    const ghostTree = ghost ? this.ghostTree(m) : { tree: null, note: '' };
    const panels = panelsFor(m);
    const allHidden = panels.every((p) => this.hiddenPanels.has(p.id));
    return html`<div class="vg-wrapper model-workspace">
      <div class="vg-toolbar">
        <div class="vg-toolbar-actions model-workspace-toggles">
          ${panels.map((p) =>
            button({
              label: this.panelLabel(p.id),
              color: this.hiddenPanels.has(p.id) ? 'blue' : 'red',
              onClick: () => this.togglePanel(p.id),
            }),
          )}
        </div>
      </div>
      <div class="model-workspace-canvas">
        <vitesse-panel-grid
          .panels=${panels}
          .layout=${this.layout}
          .hiddenIds=${[...this.hiddenPanels]}
          @layout-change=${this.onLayoutChange}
          @panel-remove=${this.onPanelRemove}
        >
          <div slot="statechart" class="model-statechart-tile" data-ghost-tree=${ghost ? this.treeLoad() : nothing}>
            ${m.children.length > 0
              ? html`<div class="model-ghost-toggle">
                  ${ghostTree.note ? html`<span class="model-ghost-note">${ghostTree.note}</span>` : nothing}
                  ${button({
                    icon: 'ghost',
                    title: t('model.ghostTree'),
                    color: 'blue',
                    size: 'sm',
                    active: this.ghost,
                    pressed: this.ghost,
                    onClick: () => this.toggleGhost(),
                  })}
                </div>`
              : nothing}
            <vitesse-statechart
              .scxml=${this.scxml}
              .active=${active}
              .selectable=${ghost}
              .selected=${ghost ? this.treeState : ''}
              .tree=${ghostTree.tree}
              @state-select=${this.onStateSelect}
            ></vitesse-statechart>
          </div>
          <vitesse-model-variables slot="variables" bare .models=${this.models} .modelId=${this.modelId}></vitesse-model-variables>
          <vitesse-model-commands slot="commands" bare .models=${this.models} .modelId=${this.modelId}></vitesse-model-commands>
          <vitesse-model-bindings
            slot="bindings"
            bare
            .models=${this.models}
            .modelId=${this.modelId}
            .controllers=${this.controllers}
            .gateways=${this.gateways}
            .backend=${this.backend}
            .gatewayId=${this.gatewayId}
            .scxml=${this.scxml}
          ></vitesse-model-bindings>
          <vitesse-model-activity
            slot="activity"
            bare
            .models=${this.models}
            .modelId=${this.modelId}
            .api=${this.aiApi}
          ></vitesse-model-activity>
          <vitesse-model-simulate
            slot="simulate"
            bare
            .models=${this.models}
            .modelId=${this.modelId}
            .backend=${this.backend}
            .gatewayId=${this.gatewayId}
            .controllers=${this.controllers}
            .scxml=${this.scxml}
            .api=${this.aiApi}
          ></vitesse-model-simulate>
          ${m.children.length > 0
            ? html`<vitesse-model-machines slot="machines" bare .models=${this.models} .modelId=${this.modelId}></vitesse-model-machines>`
            : nothing}
        </vitesse-panel-grid>
        ${allHidden ? html`<div class="model-workspace-empty">${t('model.noComponents')}</div>` : nothing}
      </div>
    </div>`;
  }

  /** Stable source loader for the code-browser (reads the current scxml at call
   *  time). The block key carries the model id, so switching models reassembles. */
  private readonly loadScxmlSource = async (): Promise<string> => this.scxml;
  /** STABLE connect for the log tree — same discipline as loadScxmlSource above.
   *  A fresh arrow each render changes the `.connect` binding, so Lit re-renders
   *  the whole <vitesse-log-tree> (re-evaluating its entire row list) on EVERY
   *  parent render — and a running model re-renders this page at its
   *  state-transition rate (~30/s for a fast cycler), which pegs the page once the
   *  log list grows. Bound once; reads models/modelId at call time (the log-tree's
   *  willUpdate re-subscribes only when subscriptionKey changes, not `.connect`). */
  private readonly connectLogs = (): Promise<MessagePort> => this.models!.subscribeLogs(this.modelId);
  /** Cached single-element blocks array, stable per model and document — see renderScxml. */
  private scxmlBlocks?: { id: string; scxml: string; arr: CodeBlock[] };

  /*
   * WHAT THE SCXML VIEW CHECKS AGAINST: the model's declared interface, its children and its
   * profile, as the assistant's tools check a document (model-check.ts). One object for as long as
   * they stay the same — the page renders on every live tick, and a new object each time would
   * check the whole document again each time.
   */
  private scxmlCheckFor?: { key: string; options: CheckOptions };
  private get scxmlCheck(): CheckOptions | undefined {
    const m = this.model;
    if (!m) return undefined;
    // The declared types too: the type check of a `typescript` document reads them (Part 3). And a
    // group's children with theirs (Part 4): the root's document reads their ports by name. An array
    // child also gives its count and its wired inputs (Step 4), as the save's check does.
    const options: CheckOptions = {
      inputs: m.inputs,
      outputs: m.outputs,
      commands: m.commands,
      types: portTypes(m.iface),
      ...(m.children.length > 0 ? { children: childPorts(m.children) } : {}),
      // A hard model is checked with the rules of the hard profile, as the relay checks its save.
      ...(this.profile ? { profile: this.profile } : {}),
    };
    const key = JSON.stringify(options);
    if (this.scxmlCheckFor?.key !== key) this.scxmlCheckFor = { key, options };
    return this.scxmlCheckFor.options;
  }

  /*
   * WHAT THIS ORG CAN INVOKE, handed to the editor.
   *
   * The completions are the live catalog — this org's experts and the tools of the integrations it
   * has actually connected, schemas included — so an author is offered what exists rather than what
   * a docs page once said. Fetched when the SCXML tab is first opened (not at page load: an author
   * reading the statechart should not pay for it) and refreshed on every open, because an
   * integration connected five minutes ago is exactly the one being written about.
   *
   * Failure is silent BY DESIGN: with no catalog the invoke snippets still complete, which is the
   * useful floor. An editor that refused to open because the AI plane was unreachable would be a
   * worse answer than one that completes a little less.
   */
  private async loadCompletionCatalog(): Promise<void> {
    if (!this.aiApi) return;
    const [{ setScxmlCatalog }, tools] = await Promise.all([import('./scxml-completions'), this.aiApi.listTools()]);
    setScxmlCatalog({
      // The expert roster comes from the models the org configured; the plane's tool catalog carries
      // the rest. Experts are named by the invoke, so an empty roster simply completes nothing.
      experts: this.experts,
      tools,
    });
  }

  // ── SCXML tab — the EXACT program-view code-browser from the controller page
  //    (same .vg-wrapper frame, search toolbar, fold-all, scroll + minimap). The
  //    "program" is one block: the chart XML. ──────────────────────────────────
  private renderScxml() {
    void this.loadCompletionCatalog();
    if (!this.scxml) {
      return html`<div class="model-scxml-pane model-scxml-empty">${t('model.statechartComing')}</div>`;
    }
    // STABLE blocks identity per model and document: a fresh array each render would make the
    // code-browser re-assemble (refetch + reset fold/scroll) on every live tick. A saved document
    // must re-assemble, or the tab keeps the text from before the save.
    if (this.scxmlBlocks?.id !== this.modelId || this.scxmlBlocks.scxml !== this.scxml) {
      this.scxmlBlocks = {
        id: this.modelId,
        scxml: this.scxml,
        arr: [
          {
            key: `scxml:${this.modelId}`,
            label: `${safeName(this.model?.name || this.modelId || 'model')}.scxml`,
            language: 'xml',
          },
        ],
      };
    }
    return html`<vitesse-code-browser
      searchMode="scxml"
      .blocks=${this.scxmlBlocks.arr}
      .loadSource=${this.loadScxmlSource}
      .scxmlCheck=${this.scxmlCheck}
      searchPlaceholder=${t('grid.searchPlaceholder')}
      emptyHint=${t('model.statechartComing')}
    ></vitesse-code-browser>`;
  }

  // ── Logs tab — the SHARED log renderer, pixel-identical to the controller logs ─
  private renderLogs() {
    const ready = !!this.models && !!this.modelId;
    return html`<vitesse-log-tree
      .subscriptionKey=${ready ? this.modelId : ''}
      .connect=${ready ? this.connectLogs : undefined}
      .toLine=${ModelDetail.logToLine}
    ></vitesse-log-tree>`;
  }

  /** ModelLog → the uniform LogLine (title-only; the model-runner doesn't emit a
   *  detail body yet — same mapping as <vitesse-model-logs>). */
  private static logToLine(item: unknown): LogLine {
    const l = item as ModelLog;
    // Use BOTH the title and the body (sub-detail) like the controller logs do —
    // the model-runner emits the body on LogEvent.body (empty → collapsed one-liner).
    return { tsMs: l.tsMs, severity: l.severity, title: l.message, body: l.body || undefined };
  }
}

/** The root state that the ghost tree hangs under when it turns on: the first active state that the text has, else its first state. */
function firstTreeState(current: string, ports: ReadonlyMap<string, unknown>): string {
  const active = current
    .split(',')
    .map((s) => s.trim())
    .find((id) => ports.has(id));
  return active ?? ports.keys().next().value ?? '';
}

/** Each template's text by its name. Null when the read has no descriptor: the relay is older than Part 5 of the Step 3 plan. */
function templatesOf(d: ModelSource['descriptor']): Record<string, string> | null {
  if (!d) return null;
  return Object.fromEntries(Object.entries(d.templates).map(([name, template]) => [name, template.scxml]));
}

// ── download helpers ───────────────────────────────────────────────────────────
function safeName(name: string): string {
  return (name || 'model').replace(/[^\w.-]+/g, '_');
}
function triggerDownload(filename: string, content: string, mime: string): void {
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
function samplesToCsv(samples: HistorySample[]): string {
  const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
  const rows = ['timestamp_ms,variable,value'];
  for (const s of samples) rows.push(`${s.tsMs},${esc(s.symbol)},${s.num}`);
  return rows.join('\n');
}

declare global {
  interface HTMLElementTagNameMap {
    'vitesse-model-detail': ModelDetail;
  }
}
